import { useEffect, useMemo, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { Banner } from '../components/Banner';
import { useWorkspace } from '../store/workspace';
import { fold } from '../lib/sorting';
import { eventTime } from '../lib/when';
import { describeDetail, roleName } from '../lib/activity';

interface AuditEvent {
  id: string;
  at: string;
  action: string;
  actor: { id: string | null; name: string; role: string };
  subject: { id: string | null; label: string | null };
  organizationId: string | null;
  detail: Record<string, unknown> | null;
}

/**
 * What each verb means, said the way a person would say it.
 *
 * The stored action is a dotted verb because it is filtered and counted; this
 * is the sentence built around it. An action with no entry here still reads —
 * it falls back to the verb itself rather than to nothing.
 */
const PHRASE: Record<string, string> = {
  'student.enrolled': 'enrolled',
  'students.enrolled': 'enrolled a class into',
  'students.given': 'gave students to',
  'student.removed': 'removed',
  'teacher.added': 'added',
  'teacher.removed': 'removed',
  'password.reset': 'reset the password for',
  'login.locked': 'locked out after too many wrong passwords:',
  'class.renamed': 'renamed the class to',
  'class.dissolved': 'dissolved the class',
  'paper.published': 'published',
  'paper.archived': 'archived',
  'paper.deleted': 'deleted',
  'results.released': 'released the results for a paper',
  'results.withheld': 'withheld the results for a paper',
  'results.remarked': 'marked a paper again',
  'sitting.cleared': 'cleared a sitting',
  'assignment.withdrawn': 'withdrew a paper from a student',
  'centre.created': 'created the centre',
  'files.exported': 'downloaded every uploaded file, to move servers',
  'centre.changed': 'changed the centre',
  'centre.suspended': 'suspended the centre',
  'paper.shelved': 'moved to the past papers',
  'paper.brought-back': 'brought back to the current papers',
  'seb.required': 'required Safe Exam Browser at',
  'seb.not-required': 'stopped requiring Safe Exam Browser at',
  'session.scheduled': 'put a session in the diary',
  'session.set': 'put the papers on the desks for',
  'session.shuffled': 'dealt the papers around the room for',
  'session.unshuffled': 'stopped dealing the papers for',
  'session.closed': 'closed the session',
  'session.deleted': 'took a session out of the diary',
};

/** Destructive verbs are worth seeing at a glance in a long list. */
const GRAVE = new Set([
  'student.removed',
  'teacher.removed',
  'paper.deleted',
  'sitting.cleared',
  'class.dissolved',
  'centre.suspended',
  'login.locked',
  'session.deleted',
]);

/**
 * The record of what was done, and by whom.
 *
 * Read after the fact, usually about something that no longer exists, so the
 * names in it are the names as they were — this screen does not join back to
 * anything, and that is deliberate.
 */
export function ActivityPage() {
  const organizationId = useWorkspace((w) => w.organizationId);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [action, setAction] = useState('');
  const [actions, setActions] = useState<string[]>([]);

  async function load() {
    setLoading(true);
    try {
      const [list, verbs] = await Promise.all([
        api.get<{ events: AuditEvent[]; total: number }>('/audit', {
          params: { limit: 200, ...(action ? { action } : {}) },
        }),
        api.get<{ actions: string[] }>('/audit/actions'),
      ]);
      setEvents(list.data.events);
      setTotal(list.data.total);
      setActions(verbs.data.actions);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the activity log'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, action]);

  // Searched here: the page holds what it was given, and the term is matched
  // against the words actually on screen — a name, a verb, an address.
  const shown = useMemo(() => {
    const needle = fold(query);
    if (!needle) return events;
    return events.filter(
      (event) =>
        fold(event.actor.name).includes(needle) ||
        fold(event.subject.label ?? '').includes(needle) ||
        fold(event.action).includes(needle),
    );
  }, [events, query]);

  return (
    <>
      <PageHeader
        title="Activity"
        meta={
          <p className="muted" style={{ maxWidth: '62ch' }}>
            Everything that changed an account, a paper or a result — who did it, to what, and
            when. Written as it happened and never rewritten, so a name here is the name that was
            on the screen at the time.
          </p>
        }
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}

        <div className="listbar">
          <input
            type="search"
            className="field__input listbar__search"
            placeholder="Find by person, subject or address"
            aria-label="Find an entry by person, subject, action or address"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="row">
            <select
              className="field__input"
              style={{ maxWidth: '16rem' }}
              aria-label="Only one kind of action"
              value={action}
              onChange={(e) => setAction(e.target.value)}
            >
              <option value="">Everything</option>
              {actions.map((verb) => (
                <option key={verb} value={verb}>
                  {verb}
                </option>
              ))}
            </select>
            <p className="muted">
              <span className="mono">{query.trim() ? shown.length : total}</span>{' '}
              {total === 1 ? 'entry' : 'entries'}
            </p>
          </div>
        </div>

        {loading ? (
          <p className="muted">Loading…</p>
        ) : shown.length === 0 ? (
          <div className="empty">
            <p>{query.trim() || action ? 'Nothing matches.' : 'Nothing has happened yet.'}</p>
            <p className="muted">
              Enrolling a student, removing one, resetting a password or deleting a paper all
              appear here.
            </p>
          </div>
        ) : (
          <div className="ledger__wrap">
            <table className="ledger">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Did what</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((event) => (
                  <tr key={event.id}>
                    <td className="activity__when" title={new Date(event.at).toLocaleString('en-GB')}>
                      {eventTime(event.at)}
                    </td>
                    <td>
                      <span className="activity__who">{event.actor.name}</span>
                      <span className="activity__role">{roleName(event.actor.role)}</span>
                    </td>
                    <td>
                      <span className={GRAVE.has(event.action) ? 'activity--grave' : undefined}>
                        {PHRASE[event.action] ?? event.action}
                      </span>{' '}
                      {event.subject.label && (
                        <span style={{ fontWeight: 600 }}>{event.subject.label}</span>
                      )}
                      {describeDetail(event.detail).length > 0 && (
                        <span className="activity__detail">
                          {describeDetail(event.detail).join(' · ')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
